import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";

import {
  LIFECYCLE_STATES,
  SAIcon,
  STATE_TONE,
  TYPE_META,
  fmtAwstShort,
  isDraft,
  scopeLabel,
  stateLabel,
  stateOf,
  triageColumn,
  typeOf,
  type TriageColumn,
} from "../components/statusAlerts";
import { api, type AnnouncementAdmin, type Me } from "../services/api";

/**
 * Admin · Status Alerts (Implementation Spec v1.2). Replaces the flat
 * Announcements table+modal with two complementary surfaces:
 *
 * - Triage (default): a lifecycle board — Drafts · Active now · Scheduled &
 *   upcoming · Resolved & shipped — answering "what is live and who is waiting
 *   on the next update."
 * - Timeline: a reverse-chronological, day-grouped feed evidencing the Incident
 *   Response cadence (the audit lens).
 *
 * iQuest staff (iquest_admin / service_desk) only; targeting flows one-way
 * from iQuest staff to client tenants. Posting/editing happens in the full-page composer
 * (AdminStatusAlertComposer) at /admin/announcements/{new,:id}.
 */
export function AdminAnnouncements() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());

  if (!me) {
    return (
      <>
        <div className="sa-head">
          <h1 className="sa-title">Status Alerts</h1>
        </div>
        <div className="card">Loading…</div>
      </>
    );
  }

  if (me.role !== "iquest_admin" && me.role !== "service_desk") {
    return (
      <>
        <div className="sa-head">
          <h1 className="sa-title">Status Alerts</h1>
        </div>
        <div className="card empty-state">
          <p>You don&apos;t have access to this page.</p>
          <p className="empty-state-blurb">
            Status alerts are managed by iQuest staff.
          </p>
        </div>
      </>
    );
  }

  return <AdminAnnouncementsInner />;
}

type FilterKey = "all" | "live" | "outage" | "maintenance" | "enhancement" | "drafts";
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "live", label: "Live" },
  { key: "outage", label: "Outage" },
  { key: "maintenance", label: "Maintenance" },
  { key: "enhancement", label: "Enhancement" },
  { key: "drafts", label: "Drafts" },
];

function matchesFilter(a: AnnouncementAdmin, f: FilterKey): boolean {
  switch (f) {
    case "all":
      return true;
    case "live":
      return a.is_active;
    case "drafts":
      return isDraft(a);
    case "outage":
    case "maintenance":
    case "enhancement":
      return typeOf(a) === f;
  }
}

/**
 * True when the alert reached `companyId`. An all-clients broadcast reaches
 * every client, so it counts as targeting the selected one — the timeline is
 * the audit lens, and a broadcast outage is part of that client's history.
 * Empty `companyId` means "no client filter" and matches everything.
 */
function matchesClient(a: AnnouncementAdmin, companyId: string): boolean {
  if (!companyId) return true;
  if (a.target_all) return true;
  return a.target_company_ids?.includes(companyId) ?? false;
}

function AdminAnnouncementsInner() {
  const navigate = useNavigate();
  const { data, isLoading } = useSWR("/api/admin/announcements", () =>
    api.announcements.adminList(1, 200),
  );
  const { data: clients } = useSWR("sa-clients", () =>
    api.announcements.listClients(),
  );

  const [filter, setFilter] = useState<FilterKey>("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"triage" | "timeline">("triage");
  // Client filter is timeline-only — the audit lens for a single tenant.
  const [clientFilter, setClientFilter] = useState("");

  const items = useMemo(() => data?.items ?? [], [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(
      (a) =>
        matchesFilter(a, filter) &&
        (view !== "timeline" || matchesClient(a, clientFilter)) &&
        (!q ||
          a.title.toLowerCase().includes(q) ||
          (a.reference ?? "").toLowerCase().includes(q)),
    );
  }, [items, filter, query, view, clientFilter]);

  const clientName = clients?.find((c) => c.id === clientFilter)?.name;

  const stats = useMemo(() => {
    const counts = { drafts: 0, active: 0, scheduled: 0, resolved: 0 };
    for (const a of items) counts[triageColumn(a)] += 1;
    return counts;
  }, [items]);

  return (
    <div>
      <div className="sa-head">
        <div>
          <h1 className="sa-title">Status Alerts</h1>
          <p className="sa-sub">
            Post outages, scheduled maintenance and enhancements. Each alert
            drives the in-portal banner and the email sent to the clients you
            target. It also tracks its own lifecycle of updates.
          </p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/admin/announcements/new")}
          >
            <span style={{ display: "inline-flex", width: 15, height: 15 }}>
              <SAIcon name="plus" />
            </span>{" "}
            New status alert
          </button>
        </div>
      </div>

      <StatStrip stats={stats} />

      <div className="sa-toolbar">
        <div className="filter-tabs" style={{ margin: 0 }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`filter-tab ${filter === f.key ? "active" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="sa-toolbar-spacer" />
        {view === "timeline" && (
          <label className="sa-clientsel">
            <span style={{ display: "inline-flex", width: 14, height: 14 }}>
              <SAIcon name="building" />
            </span>
            <select
              className={clientFilter ? "active" : ""}
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              aria-label="Filter timeline by client"
            >
              <option value="">All clients</option>
              {clients?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="search-bar" style={{ minWidth: 180, maxWidth: 240 }}>
          <SAIcon name="search" />
          <input
            type="text"
            placeholder="Search alerts…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="sa-segged">
          <button
            type="button"
            className={view === "triage" ? "active" : ""}
            onClick={() => setView("triage")}
          >
            <span style={{ display: "inline-flex", width: 14, height: 14 }}>
              <SAIcon name="server" />
            </span>
            Triage
          </button>
          <button
            type="button"
            className={view === "timeline" ? "active" : ""}
            onClick={() => setView("timeline")}
          >
            <span style={{ display: "inline-flex", width: 14, height: 14 }}>
              <SAIcon name="activity" />
            </span>
            Timeline
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="card">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card empty-state">
          <p>No status alerts yet.</p>
          <p className="empty-state-blurb">
            Create one to broadcast a service status to clients.
          </p>
        </div>
      ) : view === "triage" ? (
        <TriageBoard items={filtered} />
      ) : (
        <TimelineView items={filtered} clientName={clientName} />
      )}
    </div>
  );
}

function StatStrip({
  stats,
}: {
  stats: { drafts: number; active: number; scheduled: number; resolved: number };
}) {
  return (
    <div className="sa-stats">
      <div className="sa-stat">
        <span className="sa-stat-accent" style={{ background: "#C4262E" }} />
        <div className="sa-stat-label">
          <SAIcon name="outage" /> Live incidents
        </div>
        <div className="sa-stat-value red">{stats.active}</div>
        <div className="sa-stat-meta">
          {stats.active === 0 ? "All systems operational" : "Active outages"}
        </div>
      </div>
      <div className="sa-stat">
        <span className="sa-stat-accent" style={{ background: "#2A6FDB" }} />
        <div className="sa-stat-label">
          <SAIcon name="clock" /> Scheduled &amp; upcoming
        </div>
        <div className="sa-stat-value blue">{stats.scheduled}</div>
        <div className="sa-stat-meta">Maintenance &amp; enhancements</div>
      </div>
      <div className="sa-stat">
        <span className="sa-stat-accent" style={{ background: "#5B3FE0" }} />
        <div className="sa-stat-label">
          <SAIcon name="mail" /> Drafts
        </div>
        <div className="sa-stat-value violet">{stats.drafts}</div>
        <div className="sa-stat-meta">Saved, not yet sent</div>
      </div>
      <div className="sa-stat">
        <span className="sa-stat-accent" style={{ background: "#0A8E3E" }} />
        <div className="sa-stat-label">
          <SAIcon name="check" /> Resolved &amp; shipped
        </div>
        <div className="sa-stat-value green">{stats.resolved}</div>
        <div className="sa-stat-meta">Closed incidents + completed work</div>
      </div>
    </div>
  );
}

function StateChip({ state }: { state: string }) {
  return (
    <span className={`sa-chip ${state}`}>
      <span className="d" />
      {stateLabel(state)}
    </span>
  );
}

function ScopeBadge({ a }: { a: AnnouncementAdmin }) {
  return (
    <span className={`sa-scope-badge ${a.target_all ? "all" : ""}`}>
      <SAIcon name={a.target_all ? "globe" : "building"} />
      {scopeLabel(a)}
    </span>
  );
}

const TRIAGE_COLUMNS: { key: TriageColumn; title: string; dot: string }[] = [
  { key: "drafts", title: "Drafts", dot: "#6A6A72" },
  { key: "active", title: "Active now", dot: "#C4262E" },
  { key: "scheduled", title: "Scheduled & upcoming", dot: "#2A6FDB" },
  { key: "resolved", title: "Resolved & shipped", dot: "#0A8E3E" },
];

function TriageBoard({ items }: { items: AnnouncementAdmin[] }) {
  const byColumn = useMemo(() => {
    const map: Record<TriageColumn, AnnouncementAdmin[]> = {
      drafts: [],
      active: [],
      scheduled: [],
      resolved: [],
    };
    for (const a of items) map[triageColumn(a)].push(a);
    const recency = (a: AnnouncementAdmin) =>
      new Date(a.last_update_at ?? a.created_at).getTime();
    for (const k of Object.keys(map) as TriageColumn[]) {
      map[k].sort((x, y) => recency(y) - recency(x));
    }
    return map;
  }, [items]);

  return (
    <div className="sa-board">
      {TRIAGE_COLUMNS.map((col) => {
        const cards = byColumn[col.key];
        return (
          <div className="sa-col" key={col.key}>
            <div className="sa-col-head">
              <span className="sa-col-title">
                <span className="dot" style={{ background: col.dot }} />
                {col.title}
              </span>
              <span className="sa-col-count">{cards.length}</span>
            </div>
            <div className="sa-col-list">
              {cards.length === 0 ? (
                <div className="sa-col-empty">Nothing here</div>
              ) : (
                cards.map((a) => <BoardCard key={a.id} a={a} />)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BoardCard({ a }: { a: AnnouncementAdmin }) {
  const type = typeOf(a);
  const meta = TYPE_META[type];
  const state = stateOf(a);
  const draft = isDraft(a);
  const showReminder =
    a.is_active && type !== "outage" && a.reminder_at != null && !a.reminder_sent_at;

  return (
    <Link
      className="sa-bcard"
      to={`/admin/announcements/${a.id}`}
      style={{ ["--accent" as string]: meta.tone }}
    >
      <div className="sa-bcard-top">
        <span
          className={`sa-glyph ${type}`}
          style={{ width: 28, height: 28, borderRadius: 8 }}
        >
          <SAIcon name={meta.glyph} />
        </span>
        <span className="sa-bcard-cat">
          {meta.label}
          {a.reference ? ` · ${a.reference}` : ""}
        </span>
        <StateChip state={state} />
      </div>
      <div className="sa-bcard-nm">{a.title}</div>
      <div style={{ marginBottom: 11 }}>
        <ScopeBadge a={a} />
      </div>
      <div className="sa-bcard-foot">
        <span>
          <SAIcon name={draft ? "mail" : "users"} style={{ display: "inline" }} />
          {draft ? "Not sent" : scopeLabel(a)}
        </span>
        <span>
          <SAIcon name="activity" style={{ display: "inline" }} />
          {a.update_count} upd
        </span>
      </div>
      {showReminder && (
        <div className="sa-bcard-rem">
          <SAIcon name="bell" />
          Reminder queued · {fmtAwstShort(a.reminder_at)}
        </div>
      )}
    </Link>
  );
}

function dayKey(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", {
    timeZone: "Australia/Perth",
  });
}

function dayHeading(iso: string): string {
  const today = dayKey(new Date().toISOString());
  const yest = dayKey(new Date(Date.now() - 86_400_000).toISOString());
  const k = dayKey(iso);
  const label = new Date(iso).toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "Australia/Perth",
  });
  if (k === today) return `Today · ${label}`;
  if (k === yest) return `Yesterday · ${label}`;
  return label;
}

function TimelineView({
  items,
  clientName,
}: {
  items: AnnouncementAdmin[];
  clientName?: string;
}) {
  const groups = useMemo(() => {
    const when = (a: AnnouncementAdmin) => a.last_update_at ?? a.created_at;
    const sorted = [...items].sort(
      (x, y) => new Date(when(y)).getTime() - new Date(when(x)).getTime(),
    );
    const out: { day: string; iso: string; rows: AnnouncementAdmin[] }[] = [];
    for (const a of sorted) {
      const k = dayKey(when(a));
      const last = out[out.length - 1];
      if (last && last.day === k) last.rows.push(a);
      else out.push({ day: k, iso: when(a), rows: [a] });
    }
    return out;
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="card empty-state">
        <p>{clientName ? `No alerts for ${clientName}.` : "Nothing matches."}</p>
        {clientName && (
          <p className="empty-state-blurb">
            No status alerts have targeted this client (or all clients) in the
            current view.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: "24px 28px 14px" }}>
      {clientName && (
        <div className="sa-tl-scope">
          <SAIcon name="building" />
          <span>
            Showing alerts for <b>{clientName}</b> · includes all-client
            broadcasts
          </span>
        </div>
      )}
      <div className="sa-tl">
        {groups.map((g) => (
          <div key={g.day}>
            <div className="sa-tl-daygroup">{dayHeading(g.iso)}</div>
            {g.rows.map((a) => (
              <TimelineRow key={a.id} a={a} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function TimelineRow({ a }: { a: AnnouncementAdmin }) {
  const type = typeOf(a);
  const meta = TYPE_META[type];
  const state = stateOf(a);
  const draft = isDraft(a);
  const tone = STATE_TONE[state] ?? meta.tone;
  const action = draft ? "Continue" : a.is_active ? "Post update" : "View";

  return (
    <div className="sa-tl-item" style={{ ["--node" as string]: tone }}>
      <span className="sa-tl-dot" />
      <div className="sa-tl-row">
        <div className="sa-tl-rowtop">
          <span className={`sa-glyph ${type}`}>
            <SAIcon name={meta.glyph} />
          </span>
          <div className="hd">
            <div className="sa-tl-nm">{a.title}</div>
            <div className="sa-tl-rf">
              {meta.label}
              {a.reference ? ` · ${a.reference}` : ""}
            </div>
          </div>
          <StateChip state={state} />
          <span className="sa-tl-time">
            {fmtAwstShort(a.last_update_at ?? a.created_at)}
          </span>
        </div>
        <div className="sa-tl-foot">
          <span className="sa-meta-pair">
            <ScopeBadge a={a} />
          </span>
          <span className="sa-meta-pair">
            <SAIcon name="activity" />
            <b>{a.update_count ?? 0}</b> update{a.update_count === 1 ? "" : "s"}
          </span>
          {!draft && (
            <span className="sa-meta-pair">
              <SAIcon name={a.is_active ? "eye" : "check"} />
              {LIFECYCLE_STATES[type].map((s) => s[0]).includes(state)
                ? stateLabel(state)
                : "Posted"}
            </span>
          )}
          <Link
            className="btn btn-ghost btn-sm"
            to={`/admin/announcements/${a.id}`}
            style={{ marginLeft: "auto" }}
          >
            {action}
          </Link>
        </div>
      </div>
    </div>
  );
}
